<script setup lang="ts">
import { useTimesheetStore, BreakType, BreakState } from '@/stores/timesheets';
import { useRosterStore } from '@/stores/rosters';
import { getTranslations } from '@/lib/util/i18n';
import { displayFromSeconds } from '@/lib/util/dateTime';
import ShiftDetailLineItem from './ShiftDetailLineItem.vue';

const t = getTranslations({
  app: '',
  module: 'views',
  file: 'breaks-list',
});

const timesheets = useTimesheetStore();
const rosters = useRosterStore();

const props = defineProps<{
  editBreaksModal?: boolean;
  scheduledBreaks?: boolean;
  allBreaks?: boolean;
  selectBreaksModal?: boolean;
  breaks: any;
}>();

const breaks = timesheets.activeTimesheet
  ? timesheets.getBreaks()
  : rosters.getAllScheduledBreaks();

const validBreaks = timesheets.activeTimesheet
  ? timesheets.getAllValidBreaks()
  : rosters.getScheduledBreaks();

function getBreakDuration(index: number): number {
  const SECONDS_IN_MINUTE = 60;
  return (
    (breaks[index].intUnixEnd - breaks[index].intUnixStart) / SECONDS_IN_MINUTE
  );
}

function getBreakType(strBreakType: string): string {
  return strBreakType == BreakType.Unpaid ? '(unpaid)' : '(paid)';
}

function getInfoContentTranslation(breakIndex: number) {
  if (
    props.allBreaks &&
    timesheets.getBreaks()[breakIndex].strState == BreakState.InProgress
  ) {
    return t('break-start-time', {
      startBreakTime: displayFromSeconds(
        timesheets.getStartBreakTime(breakIndex)
      ),
    });
  }

  if (
    props.allBreaks &&
    timesheets.getBreaks()[breakIndex].strState == BreakState.Finished
  ) {
    const translationContent = {
      startBreakTime: displayFromSeconds(
        timesheets.getStartBreakTime(breakIndex)
      ),
      endBreakTime: displayFromSeconds(timesheets.getEndBreakTime(breakIndex)),
      breakDuration: timesheets.getFinishedBreakTotalTime(breakIndex),
    };

    return timesheets.getFinishedBreakTotalTime(breakIndex) == 1
      ? t('break-time-duration-singular', translationContent)
      : t('break-time-duration', translationContent);
  }

  if (
    props.scheduledBreaks &&
    rosters.getScheduledBreakStartTimeById(breakIndex)
  ) {
    return t('break-roster-time', {
      startBreakTime: displayFromSeconds(
        rosters.getScheduledBreakStartTimeById(breakIndex)
      ),
      endBreakTime: displayFromSeconds(
        rosters.getScheduledBreakEndTimeById(breakIndex)
      ),
    });
  }

  if (
    props.allBreaks &&
    timesheets.getBreaks()[breakIndex].strState == BreakState.NotStart
  ) {
    return t('break-roster-time', {
      startBreakTime: displayFromSeconds(
        timesheets.getBreaks()[breakIndex].intUnixStart
      ),
      endBreakTime: displayFromSeconds(
        timesheets.getBreaks()[breakIndex].intUnixEnd
      ),
    });
  }

  return null;
}

function getBreakItemClass() {
  if (props.editBreaksModal) {
    return `__edit-break-item-content`;
  } else if (props.selectBreaksModal) {
    return `__select-break-item-content`;
  } else {
    return `__break-content`;
  }
}

function isBreaksListItemVisible(slot: any) {
  const shiftCardList = !props.editBreaksModal && !props.selectBreaksModal;
  const breakMoreThanOneMinute = slot.intUnixEnd - slot.intUnixStart != 0;

  if (
    (shiftCardList || props.editBreaksModal) &&
    (breakMoreThanOneMinute ||
      (!breakMoreThanOneMinute && slot.strState == BreakState.Finished))
  ) {
    return true;
  } else if (
    props.selectBreaksModal &&
    breakMoreThanOneMinute &&
    slot.strState !== BreakState.Finished
  ) {
    return true;
  }

  return false;
}

function getNoBreaksLabel() {
  if (rosters.getRosterForActiveUser()) {
    return t('header-none-scheduled');
  }

  if (timesheets.isOnShift) {
    return t('header-none-taken');
  }
}

const emit = defineEmits<{
  (e: 'showEditBreakForm', breakId: number): void;
  (e: 'startBreak', breakType: string, breakId?: number, source?: string): void;
}>();
</script>

<template>
  <div class="breaks-list">
    <div
      :class="
        editBreaksModal || selectBreaksModal ? `__break-modal-item` : `__break`
      "
      v-for="(slot, index) in props.breaks"
      :key="slot.strType"
    >
      <div
        :class="getBreakItemClass()"
        @click="
          selectBreaksModal
            ? emit(
                'startBreak',
                slot.mixedActivity.strBreakType,
                index,
                'selectBreakType'
              )
            : null
        "
        v-if="isBreaksListItemVisible(slot)"
      >
        <span
          v-if="editBreaksModal || selectBreaksModal"
          class="__icon-background"
          :class="
            slot.mixedActivity.strBreakType === BreakType.Unpaid
              ? `__pink`
              : `__green`
          "
        >
          <img
            v-if="slot.mixedActivity.strBreakType === BreakType.Unpaid"
            class="__icon-image"
            src="../assets/images/cafe-pink.png"
          />
          <img
            v-else
            class="__icon-image"
            src="../assets/images/cafe-green.png"
          />
        </span>
        <a v-if="selectBreaksModal">
          <ShiftDetailLineItem
            :infoContent="true"
            :selectBreaksModal="selectBreaksModal"
          >
            <template #main-content>
              {{
                t('break-list-options', {
                  breakName: slot.strTypeName,
                  breakType: getBreakType(slot.mixedActivity.strBreakType),
                })
              }}
            </template>
            <template
              v-if="slot.strState == BreakState.ScheduledDuration"
              #info-content
            >
              {{
                t('break-list-roster-duration', {
                  breakDuration: getBreakDuration(index),
                })
              }}
            </template>
            <template v-else #info-content>
              {{
                t('break-roster-time', {
                  startBreakTime: displayFromSeconds(
                    timesheets.getStartBreakTime(index)
                  ),
                  endBreakTime: displayFromSeconds(
                    timesheets.getEndBreakTime(index)
                  ),
                })
              }}
            </template>
          </ShiftDetailLineItem>
        </a>

        <ShiftDetailLineItem
          v-else-if="!selectBreaksModal"
          :infoContent="Boolean(getInfoContentTranslation(index))"
          :editModalList="editBreaksModal"
        >
          <template #main-content>
            {{
              slot.intUnixEnd &&
              slot.strState !== BreakState.Finished &&
              getBreakDuration(index) >= 0
                ? t('break-roster-duration', {
                    breakDuration: getBreakDuration(index),
                    breakName: slot.strTypeName.toLowerCase(),
                    breakType: getBreakType(slot.mixedActivity.strBreakType),
                  })
                : t('break-name-type', {
                    breakName: slot.strTypeName,
                    breakType: getBreakType(slot.mixedActivity.strBreakType),
                  })
            }}
          </template>
          <template #info-content>
            {{ getInfoContentTranslation(index) }}
          </template>
        </ShiftDetailLineItem>
        <a
          class="__edit-action"
          v-if="
            editBreaksModal && timesheets.getFinishedBreakEndTimeById(index)
          "
          @click="emit('showEditBreakForm', index)"
          >{{ t('edit') }}</a
        >
      </div>
      <hr
        v-if="
          isBreaksListItemVisible(slot) &&
          (editBreaksModal || selectBreaksModal)
        "
      />
    </div>
    <div class="no-breaks" v-if="!validBreaks.length && editBreaksModal">
      <img class="__no-breaks-img" src="../assets/images/coffee-break.png" />
      <p class="__no-breaks-heading">{{ getNoBreaksLabel() }}</p>
      <p class="__no-breaks-text">{{ t('description-none') }}</p>
    </div>
    <div
      class="no-breaks"
      v-if="!validBreaks.length && !editBreaksModal && !selectBreaksModal"
    >
      <ShiftDetailLineItem
        :infoContent="false"
        :selectBreaksModal="true"
        :editModalList="true"
      >
        <template #main-content>{{ getNoBreaksLabel() }}</template>
      </ShiftDetailLineItem>
    </div>
  </div>
</template>

<style scoped>
.__break:has(div) {
  padding: 8px 0;
}

.__icon-background {
  display: flex;
  padding: 10px;
  border-radius: 100px;
  margin-right: 18px;
  justify-content: center;
  align-items: center;
  float: left;
}

.__edit-break-item-content,
.__select-break-item-content {
  display: flex;
  align-items: center;
}
.__select-break-item-content {
  cursor: pointer;
}
.__pink {
  background: var(--co-spot-icon-pink-background);
}
.__green {
  background: var(--co-spot-icon-teal-background);
}
.__icon-image {
  width: 1.3rem;
  height: 1.4rem;
}

.__edit-action {
  color: var(--co-text-highlight);
  padding: 10px;
  font-weight: 500;
  font-size: 16px;
}

hr {
  margin: 12px 0 12px 60px;
  border: none;
  height: 1px;
  background-color: var(--co-lines-border-solid);
}

.__break-options .breaks-list:last-child .__break-modal-item:last-child hr {
  display: none !important;
}

.__edit-breaks div .breaks-list:last-child .__break-modal-item:last-child hr {
  display: none !important;
}

.no-breaks {
  text-align: center;
  margin-top: 9px;
}

.__no-breaks-heading,
.__no-breaks-text {
  size: 16px;
  line-height: 24px;
  font-weight: 400px;
}

.__no-breaks-heading {
  font-weight: 600;
  margin: 24px 0 16px 0;
}

.__no-breaks-text {
  margin-bottom: -16px;
}

.breaks-list {
  margin-left: -2px;
}
</style>
